
import { useState, useEffect } from "react"
import {
  ArrowLeft,
  Play,
  Pause,
  Copy,
  Edit,
  MoreHorizontal,
  Slack,
  Mail,
  Calendar,
  Video,
  Phone,
  FileText,
  MessageSquare,
} from "lucide-react"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Badge } from "./ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu"
import { ScrollArea } from "./ui/scroll-area"

interface TrackerDetailProps {
  trackerId: string
  tracker?: TrackerConfig | null
  hits?: TrackerHit[]
  isLoading?: boolean
  error?: string | null
  onRetry?: () => void
  onEdit: () => void
  onBack: () => void
}

interface TrackerHit {
  id: string
  matchedAt: string
  source: string
  snippet: string
  triggeredEmotions: Array<{
    name: string
    value: number
    threshold: number
  }>
  valence: number
  arousal: number
  metadata: {
    channel?: string
    participants?: string[]
    duration?: number
  }
}

interface TrackerConfig {
  id: string
  name: string
  description: string
  visibility: "private" | "team" | "organization"
  status: "active" | "paused"
  sources: string[]
  rules: {
    keywords: string[]
    emotions: Array<{ name: string; threshold: number }>
    valenceRange: [number, number]
    arousalRange: [number, number]
  }
  actions: {
    notifications: string[]
    cooldownSeconds: number
  }
  createdAt: string
  updatedAt: string
  version: number
}

const sourceIcons: Record<string, typeof Slack> = {
  slack: Slack,
  gmail: Mail,
  gcal: Calendar,
  zoom: Video,
  gong: Phone,
  voice: Phone,
  tickets: FileText,
  notes: MessageSquare,
}

export function TrackerDetail({
  trackerId,
  tracker,
  hits = [],
  isLoading = false,
  error,
  onRetry,
  onEdit,
  onBack,
}: TrackerDetailProps) {
  const [status, setStatus] = useState<"active" | "paused">(tracker?.status ?? "active")

  useEffect(() => {
    if (tracker?.status) {
      setStatus(tracker.status)
    }
  }, [tracker?.status])

  const toggleTrackerStatus = () => {
    setStatus((prev) => (prev === "active" ? "paused" : "active"))
  }

  const duplicateTracker = () => {
    // In real app: navigate to builder with pre-filled data
    console.log("Duplicate tracker", trackerId)
  }

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString()
  }

  const getSourceIcon = (source: string) => {
    const Icon = sourceIcons[source as keyof typeof sourceIcons]
    return Icon ? <Icon className="h-4 w-4" /> : null
  }

  const getEmotionColor = (emotion: string) => {
    // Simple color mapping - in real app, use valence-based coloring
    const colors = {
      Confusion: "bg-orange-100 text-orange-800",
      Frustration: "bg-red-100 text-red-800",
      Joy: "bg-green-100 text-green-800",
      Excitement: "bg-blue-100 text-blue-800",
    }
    return colors[emotion as keyof typeof colors] || "bg-gray-100 text-gray-800"
  }

  const getVisibilityLabel = (visibility: string) => {
    // Map API values to display values
    if (visibility === "public") return "Organization"
    if (visibility === "private") return "Private"
    if (visibility === "team") return "Team"
    if (visibility === "organization") return "Organization"
    return visibility
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
          <div className="max-w-3xl">
            <h1 className="text-3xl font-bold break-words">
              {tracker?.name || (isLoading ? "Loading tracker..." : "Tracker not found")}
            </h1>
            <p className="text-muted-foreground whitespace-pre-wrap break-words">
              {tracker?.description ||
                (isLoading ? "Fetching tracker details..." : "No description available for this tracker.")}
            </p>
          </div>
        </div>

        {tracker && (
          <div className="flex items-center gap-2">
            <Badge variant={status === "active" ? "default" : "secondary"}>{status}</Badge>

            <Button variant="outline" onClick={toggleTrackerStatus} className="flex items-center gap-2 bg-transparent">
              {status === "active" ? (
                <>
                  <Pause className="h-4 w-4" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Resume
                </>
              )}
            </Button>

            <Button variant="outline" onClick={onEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={duplicateTracker}>
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {error && (
        <Card>
          <CardContent className="py-6">
            <div className="flex items-start justify-between gap-4">
              <p className="text-sm text-red-600">{error}</p>
              {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                  Try Again
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading && !tracker && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">Loading tracker details…</CardContent>
        </Card>
      )}

      {!isLoading && !tracker && !error && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Tracker details are unavailable right now. Please try again later.
          </CardContent>
        </Card>
      )}

      {tracker && (
        <Tabs defaultValue="hits" className="w-full">
          <TabsList>
            <TabsTrigger value="hits">Recent Hits ({hits.length})</TabsTrigger>
            <TabsTrigger value="config">Configuration</TabsTrigger>
          </TabsList>

          <TabsContent value="hits" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Recent Matches</CardTitle>
              </CardHeader>
              <CardContent>
                {hits.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">
                    No recent matches found for this tracker.
                  </div>
                ) : (
                  <ScrollArea className="h-[600px]">
                    <div className="space-y-4">
                      {hits.map((hit) => (
                        <Card key={hit.id} className="p-4">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {getSourceIcon(hit.source)}
                                <span className="font-medium capitalize">{hit.source}</span>
                                <span className="text-sm text-muted-foreground">{formatTimestamp(hit.matchedAt)}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <span>Valence: {hit.valence.toFixed(2)}</span>
                                <span>Arousal: {hit.arousal.toFixed(2)}</span>
                              </div>
                            </div>

                            <div className="bg-muted p-3 rounded-lg">
                              <p className="text-sm italic">"{hit.snippet}"</p>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              {hit.triggeredEmotions.map((emotion) => (
                                <Badge key={emotion.name} className={getEmotionColor(emotion.name)}>
                                  {emotion.name}: {emotion.value.toFixed(2)}
                                  (≥{emotion.threshold})
                                </Badge>
                              ))}
                            </div>

                            {(hit.metadata?.channel || hit.metadata?.duration || hit.metadata?.participants) && (
                              <div className="text-xs text-muted-foreground">
                                {hit.metadata.channel && <span>Channel: {hit.metadata.channel} • </span>}
                                {hit.metadata.duration && <span>Duration: {hit.metadata.duration}min • </span>}
                                {hit.metadata.participants && (
                                  <span>Participants: {hit.metadata.participants.length}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="config" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Name</div>
                    <div>{tracker.name}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Description</div>
                    <div>{tracker.description}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Visibility</div>
                    <Badge>{getVisibilityLabel(tracker.visibility)}</Badge>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Sources</div>
                    <div className="flex gap-2 mt-1">
                      {tracker.sources && tracker.sources.length > 0 ? (
                        tracker.sources.map((source) => (
                          <div key={source} className="flex items-center gap-1">
                            {getSourceIcon(source)}
                            <span className="text-sm capitalize">{source}</span>
                          </div>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground">No sources configured</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Detection Rules</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Keywords</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {tracker.rules.keywords && tracker.rules.keywords.length > 0 ? (
                        tracker.rules.keywords.map((keyword) => (
                          <Badge key={keyword} variant="outline">
                            {keyword}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground">None</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Emotions</div>
                    <div className="space-y-1 mt-1">
                      {tracker.rules.emotions && tracker.rules.emotions.length > 0 ? (
                        tracker.rules.emotions.map((emotion) => (
                          <div key={emotion.name} className="text-sm">
                            {emotion.name} ≥ {emotion.threshold}
                          </div>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground">None</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Valence Range</div>
                    <div className="text-sm">
                      {tracker.rules.valenceRange[0]} to {tracker.rules.valenceRange[1]}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Arousal Range</div>
                    <div className="text-sm">
                      {tracker.rules.arousalRange[0]} to {tracker.rules.arousalRange[1]}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Actions & Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Notifications</div>
                    <div className="flex gap-1 mt-1">
                      {tracker.actions.notifications && tracker.actions.notifications.length > 0 ? (
                        tracker.actions.notifications.map((notification) => (
                          <Badge key={notification} variant="outline">
                            {notification}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground">None</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Cooldown</div>
                    <div className="text-sm">{tracker.actions.cooldownSeconds} seconds</div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Version Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Version</div>
                    <div>{tracker.version}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Created</div>
                    <div className="text-sm">{formatTimestamp(tracker.createdAt)}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Last Updated</div>
                    <div className="text-sm">{formatTimestamp(tracker.updatedAt)}</div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Raw Configuration (JSON)</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <pre className="text-xs bg-muted p-4 rounded-lg overflow-auto">{JSON.stringify(tracker, null, 2)}</pre>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}